import React from 'react';

import { Navigate, useParams } from 'react-router-dom';

import { Spinner } from '@chakra-ui/react';

import { useProtectedRouteLogic } from 'components/common/ProtectedRoute/useProtectedRouteLogic';
import { useTeam } from 'queries/teams';

interface ProtectedTeamRouteV2Props {
  element: React.ReactNode;
  requiredPermission: string;
  signalsResource?: boolean;
  teamPermission?: string;
  allowEither?: boolean;
}

/**
 * Protected route component that checks authorization for team-level access.
 *
 * This component protects routes that require access to a specific team.
 * It fetches the team data using the team id from URL parameters and then
 * checks if the user has the required permissions for that team.
 *
 * @param element - The React element to render if authorization passes
 * @param requiredPermission - The permission string required to access this route
 * @param signalsResource - Whether this route signals a resource (default: false)
 * @param teamPermission - Optional specific team permission to check (default: undefined)
 * @param allowEither - Whether to allow access if either the main permission OR team permission is granted (default: false)
 *
 * @example
 * ```tsx
 * <ProtectedTeamRouteV2
 *   element={<TeamDashboard />}
 *   requiredPermission="manage_teams"
 *   teamPermission="manage_users"
 * />
 * ```
 */
const ProtectedTeamRouteV2 = ({
  element,
  requiredPermission,
  signalsResource = false,
  teamPermission,
  allowEither = false,
}: ProtectedTeamRouteV2Props) => {
  const { id: teamId } = useParams();
  const { data: team, isLoading: isTeamLoading } = useTeam(teamId);

  const { isLoading, shouldRedirect } = useProtectedRouteLogic({
    requiredPermission,
    team,
    signalsResource,
    isLoading: isTeamLoading,
    teamPermission,
    allowEither,
  });

  if (isLoading) {
    return <Spinner size="xl" />;
  }

  if (shouldRedirect) {
    return (
      <Navigate
        replace
        to={{ pathname: `/unauthorized/${requiredPermission}` }}
      />
    );
  }

  return element;
};

export default ProtectedTeamRouteV2;
